import * as React from 'react';
import { NavLink } from 'react-router-dom';
import { useTemaEscuro } from '@/hooks/use-tema-escuro';
import { Skeleton } from '@/components/ui/skeleton';
import { useAcesso } from '@/hooks/use-acesso';
import { tabsVisiveis } from '@/navigation';
import { cn } from '@/lib/utils';
import { LogoCompilacoes } from './logo';
import { GlobalRequestProgress } from './global-request-progress';
import { UserChip } from './user-chip';

/**
 * AppShell de topbar única: sem barra lateral, o conteúdo ocupa a largura toda.
 *
 *   ┌───────────────────────────────────────────────────────────┐
 *   │ [logo] Compilações │ Feed  Ramos …                 [User] │  64px
 *   ├───────────────────────────────────────────────────────────┤
 *   │ Conteúdo (bg-page), rola sozinho                          │
 *   └───────────────────────────────────────────────────────────┘
 *
 * Os tabs vêm de `navegacaoPrincipal`. Abaixo de 768px a mesma `<nav>` quebra
 * para uma faixa rolável horizontal logo abaixo da topbar (flex-wrap + order),
 * sem duplicar o landmark. Itens administrativos ficam no menu do usuário.
 */

interface AppShellProps {
  children: React.ReactNode;
  usuario: { nome: string; detalhe: string } | null;
  roles: string[];
}

const tabBase =
  'relative flex h-9 shrink-0 items-center gap-2 whitespace-nowrap rounded-button px-3 text-body transition-colors duration-fast ease-standard';

export function AppShell({ children, usuario, roles }: AppShellProps) {
  const [escuro, setEscuro] = useTemaEscuro();
  const { acesso, carregando } = useAcesso();
  const tabs = tabsVisiveis(acesso);

  return (
    <div className="flex h-full flex-col bg-bg-page">
      <GlobalRequestProgress />

      <header className="z-40 flex shrink-0 flex-wrap items-center border-b border-border bg-bg-surface px-3 md:h-topbar md:flex-nowrap md:px-4">
        {/* Marca */}
        <div className="flex h-topbar shrink-0 items-center gap-2.5 pl-1 md:h-auto">
          <LogoCompilacoes className="size-8" />
          <span className="flex flex-col leading-tight">
            <span className="text-[13px] font-bold">Compilações</span>
            <span className="text-micro font-normal text-on-surface-muted">by Nasajon</span>
          </span>
        </div>

        <span className="mx-4 hidden h-7 w-px shrink-0 bg-border md:inline-block" aria-hidden="true" />

        {/* Tabs: ao lado da marca no desktop; faixa rolável abaixo da topbar no celular. */}
        <nav
          aria-label="Principal"
          hidden={!carregando && tabs.length === 0}
          className="cmp-scroll-area order-last -mx-3 w-[calc(100%+1.5rem)] overflow-x-auto border-t border-border px-3 py-1.5 md:order-none md:mx-0 md:w-auto md:min-w-0 md:flex-1 md:border-t-0 md:p-0"
        >
          {carregando ? (
            <div role="status" aria-label="Carregando a navegação" className="flex items-center gap-1">
              {[0, 1, 2].map((indice) => (
                <Skeleton key={indice} className="h-9 w-24 rounded-button" />
              ))}
            </div>
          ) : null}
          <ul className="flex items-center gap-1">
            {(carregando ? [] : tabs).map((item) => {
              const Icone = item.icone;
              return (
                <li key={item.id} className="shrink-0">
                  <NavLink
                    to={item.rota}
                    title={item.descricao}
                    className={({ isActive }) =>
                      cn(
                        tabBase,
                        isActive ? 'bg-primary-wash font-bold text-primary' : 'text-on-surface hover:bg-bg-subtle',
                      )
                    }
                  >
                    <Icone className="size-4 shrink-0" aria-hidden="true" />
                    {item.rotulo}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>

        {usuario ? (
          <div className="ml-auto flex h-topbar shrink-0 items-center md:h-auto">
            <UserChip
              usuario={usuario}
              roles={roles}
              escuro={escuro}
              aoAlternarTema={() => setEscuro((valor) => !valor)}
            />
          </div>
        ) : null}
      </header>

      <main className="cmp-scroll-area min-w-0 flex-1 overflow-y-auto">{children}</main>
    </div>
  );
}
